import React, { useMemo, useState } from 'react';
import { confirmDialog } from '../../utils/confirm';
import { invokeMcpTool } from '../../services/mcpApi';
import McpToolForm from './McpToolForm';

export default function McpPlaygroundTab({ tools, notify }) {
  const [selectedName, setSelectedName] = useState(tools?.[0]?.name || '');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [resultError, setResultError] = useState('');

  const selected = useMemo(
    () => tools.find((t) => t.name === selectedName) || tools[0],
    [tools, selectedName],
  );

  const handleRun = async (values) => {
    if (!selected) return;
    if (selected.danger) {
      const ok = await confirmDialog({
        title: 'Run a destructive tool?',
        content: `${selected.name} is destructive and will modify data. Continue?`,
        okText: 'Run',
      });
      if (!ok) return;
    }
    setRunning(true);
    setResult(null);
    setResultError('');
    try {
      const data = await invokeMcpTool(selected, values);
      setResult(data);
      notify('success', `${selected.name} completed.`);
    } catch (err) {
      setResultError(err.message);
      notify('danger', `${selected.name} failed: ${err.message}`);
    } finally {
      setRunning(false);
    }
  };

  if (!selected) {
    return <div className="text-muted">No tools available.</div>;
  }

  return (
    <div className="row g-3">
      <div className="col-12 col-lg-5 col-xl-4">
        <div className="card shadow-sm">
          <div className="card-header bg-white fw-semibold">Tools</div>
          <div className="list-group list-group-flush" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {tools.map((tool) => (
              <button
                key={tool.name}
                type="button"
                className={`list-group-item list-group-item-action ${tool.name === selected.name ? 'active' : ''}`}
                onClick={() => { setSelectedName(tool.name); setResult(null); setResultError(''); }}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <code className={tool.name === selected.name ? 'text-white small' : 'small'}>{tool.name}</code>
                  <span className={`badge ${tool.name === selected.name ? 'bg-light text-dark' : 'bg-secondary'}`}>
                    {tool.http?.method}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="col-12 col-lg-7 col-xl-8">
        <div className="card shadow-sm mb-3">
          <div className="card-header bg-white d-flex align-items-center justify-content-between">
            <code className="fw-semibold">{selected.name}</code>
            <span className="badge bg-secondary">{selected.category}</span>
          </div>
          <div className="card-body">
            <p className="text-muted">{selected.summary}</p>
            <McpToolForm tool={selected} running={running} onRun={handleRun} />
          </div>
        </div>

        {resultError ? <div className="alert alert-danger">{resultError}</div> : null}
        {result !== null ? (
          <div className="card shadow-sm">
            <div className="card-header bg-white fw-semibold">Response</div>
            <div className="card-body">
              <pre className="bg-light border rounded p-3 mb-0 small" style={{ maxHeight: '50vh', overflow: 'auto' }}>
                <code>{JSON.stringify(result, null, 2)}</code>
              </pre>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
